import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter, map, startWith } from 'rxjs';
import { Auth, signOut } from '@angular/fire/auth';

import { TenancyService } from '../core/tenancy/tenancy.service';
import { EntitlementService } from '../core/tenancy/entitlement.service';
import { NavigationService } from '../core/navigation/navigation.service';
import { BrandThemeService } from '../core/theming/brand-theme.service';
import { NotificationService } from '../core/notifications/notification.service';
import { PermissionService } from '../core/permissions/permission.service';
import { KitchenReadyAlerts } from '../shared/components/kitchen-ready-alerts/kitchen-ready-alerts';
import { RequestsTasksDock } from './requests-tasks-dock/requests-tasks-dock';
import { ShiftFooter } from './shift-footer/shift-footer';
import { NvToastService } from '../shared/ui/toast/nv-toast';
import { NvButton } from '../shared/ui/button/nv-button';
import { NvRail } from './rail/nv-rail';
import { NvHeader } from './header/nv-header';
import { NvOfflineBanner } from './offline-banner/nv-offline-banner';
import { environment } from '../../environments/environment';

/** First URL segments rendered without the console rail/header ('' is the "/" redirect's loading page). */
const CHROME_FREE_SEGMENTS: ReadonlySet<string> = new Set(['', 'auth', 'guest', 'staff', 'kitchen', 'waiter']);

/**
 * Replaces app.html/app.ts/app.scss (Workspace Mock migration, Phase 1) — the
 * dark-rail + compact-header shell from "Workspace Mock/NexVenueX
 * Console.dc.html" §3, in place of the previous mat-toolbar + mat-sidenav
 * shell.
 *
 * Everything non-visual is carried over unchanged, not rewritten:
 *  - showShell() still matches the whole first URL segment, so `/guests`
 *    (the staff guest directory) keeps its chrome while `/guest/...` (the
 *    anonymous QR surface) does not.
 *  - BrandThemeService is still injected here purely to activate its
 *    constructor effect — dropping that injection would silently stop
 *    per-venue recolouring.
 *  - The foreground-push-listener effect keeps its `foregroundListenerStarted`
 *    guard; without it a naive rewrite double-registers the FCM handler and
 *    every push shows as two toasts.
 *  - <nv-requests-tasks-dock> (item 15) replaced the two separate
 *    <nv-request-alerts>/<nv-order-approval-strip> mounts that used to sit
 *    in page flow above the outlet — it's the mock's real floating,
 *    draggable, resizable, tabbed dock now, not a stopgap. Both components
 *    are still mounted inside it exactly as before (their round/kick/loop
 *    timing engines untouched) — only where and how they're presented
 *    changed. See RequestsTasksDock's own doc comment.
 *  - The 1024px narrow breakpoint stays in sync with
 *    shared/styles/_breakpoints.scss's $tablet-max.
 */
@Component({
  selector: 'nv-root',
  imports: [RouterOutlet, NvRail, NvHeader, RequestsTasksDock, ShiftFooter, NvButton, NvOfflineBanner, KitchenReadyAlerts],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
})
export class Shell {
  private readonly router = inject(Router);
  private readonly auth = inject(Auth);
  protected readonly tenancy = inject(TenancyService);
  private readonly entitlements = inject(EntitlementService);
  protected readonly nav = inject(NavigationService);
  private readonly notifications = inject(NotificationService);
  private readonly permissions = inject(PermissionService);
  private readonly toasts = inject(NvToastService);
  // Injected purely to activate it — its constructor effect recolours the
  // app from the venue's brand colors and keeps doing so for the session.
  private readonly brandTheme = inject(BrandThemeService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly previewFeatures = environment.previewFeatures;

  /**
   * Round 9, Pass F — mirrors ShiftFooter.visible() so a routed page can
   * react to whether the in-flow footer is eating ~50px at the bottom of
   * the content column. Used today by the AI page to size its composer's
   * clearance for the fixed requests-tasks dock.
   */
  protected readonly showShiftFooter = computed(
    () => this.previewFeatures && this.permissions.can('manage_orders')
  );

  readonly isNarrow = toSignal(
    this.breakpointObserver.observe('(max-width: 1024px)').pipe(map((state) => state.matches)),
    { initialValue: false }
  );

  /** Overlay-drawer visibility on a narrow viewport; the rail is always present as a side panel on desktop (railCollapsed narrows it there instead). */
  readonly railOpen = signal(true);
  /** Desktop-only: narrows the side rail to its icon-only state without hiding it. */
  readonly railCollapsed = signal(false);

  private foregroundListenerStarted = false;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  private readonly rootSegment = computed(() => this.url().split(/[?#]/)[0].split('/')[1] ?? '');

  /** Chrome-free routes: the guest QR surface, sign-in, and the Chef/Waiter phone screens. Matches the whole first segment so /guests (staff directory) keeps its chrome — a startsWith('/guest') test would also swallow that. */
  readonly showShell = computed(() => !CHROME_FREE_SEGMENTS.has(this.rootSegment()));

  /** The nav item whose path matches the current route, used to title the header — falls back to the segment itself so an unmapped route (e.g. /no-access) still shows something. */
  protected readonly pageTitle = computed(() => {
    const segment = this.rootSegment();
    const match = this.nav.items().find((item) => item.path.split('/')[1] === segment);
    return match?.label ?? segment;
  });

  protected readonly pageCrumb = computed(() => this.entitlements.venueName());

  constructor() {
    // Starts the foreground push listener once, the first time an eligible
    // admin/manager is signed in on an entitled venue — not on every signal
    // recompute, and not for roles this feature isn't scoped to (see
    // requireManagerOrAdmin / the manage_notifications permission).
    effect(() => {
      if (this.foregroundListenerStarted) return;
      if (!this.tenancy.ready()) return;
      const eligible =
        (this.tenancy.isAdmin() || this.tenancy.role() === 'manager') &&
        this.permissions.can('manage_notifications');
      if (!eligible) return;

      this.foregroundListenerStarted = true;
      void this.notifications.listenForegroundMessages((title, body) =>
        this.toasts.open(`${title} — ${body}`, 'Dismiss', { duration: 8000 })
      );
    });

    // Resets the drawer for the bucket it just entered — closed as an overlay
    // on a phone/tablet, open as a side panel on a laptop — without fighting a
    // manual toggle within the same bucket: this only re-fires when
    // isNarrow() itself flips (signals dedupe on equal values).
    effect(() => {
      this.railOpen.set(!this.isNarrow());
    });

    // Closes the overlay drawer after navigating on a narrow viewport —
    // otherwise it stays open covering the very page just navigated to.
    // Never touches railOpen on a wide viewport, so a desktop user who
    // manually collapsed the rail still finds it collapsed after navigating.
    effect(() => {
      this.url();
      if (this.isNarrow()) this.railOpen.set(false);
    });
  }

  toggleRail(): void {
    if (this.isNarrow()) this.railOpen.update((open) => !open);
    else this.railCollapsed.update((collapsed) => !collapsed);
  }

  /** This tab exists only because Root opened it (LicensesList.loginAsOwner) — window.close() is a no-op anywhere else, which is the correct fallback: a normal sign-out landing rather than a silently-failed close. */
  async endImpersonation(): Promise<void> {
    await signOut(this.auth);
    window.close();
    void this.router.navigate(['/auth/login']);
  }
}
